import React from 'react';
import { Button, Divider, Icon, Input, InputNumber, Popconfirm, Select, Slider, Tag } from 'antd';
import { CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS, DRAW_MODE, getMetricDetailLines } from '../../util/drawingUtils';

const { Option } = Select;

const CATEGORIES = Object.keys(CATEGORY_LABELS);

export default function DrawPanel({
  // draw state
  drawMode,
  workingPoints,
  rectangleStart,
  circleCenter,
  draftFeature,
  draftShapeType,
  draftCircle,
  draftRectangle,
  onApplyCircleGeometry,
  onApplyRectangleGeometry,
  activeMetrics,
  activeMetricsShapeType,

  // editor fields
  shapeName,
  setShapeName,
  shapeColor,
  setShapeColor,
  shapeCategory,
  setShapeCategory,
  fillOpacity,
  setFillOpacity,
  strokeWeight,
  setStrokeWeight,

  // editing
  editingExistingFence,

  // permissions
  canCreate,
  canUpdate,
  canDelete,

  // handlers
  onSetMode,
  onCancelDraw,
  onFinishPolygon,
  onUndoPoint,
  onAddToBatch,
  onUpdateExisting,
  onClearDraft,
  onDelete,
  onSaveAllPending,
  onRemovePending,
  pendingFences,
  savingFence,
  savingAllPending,
}) {
  const canDraw = canCreate || canUpdate;
  const isDrawingInProgress = drawMode !== DRAW_MODE.NONE
    || workingPoints.length > 0
    || !!rectangleStart
    || !!circleCenter;

  const metricLines = getMetricDetailLines(activeMetrics, activeMetricsShapeType, { includeCircumference: true });

  // Editable geometry values for circle / rectangle drafts.
  const cCenter = (draftCircle && draftCircle.center) || [];
  const cLat = cCenter.length ? cCenter[0] : null;
  const cLng = cCenter.length ? cCenter[1] : null;
  const cRadius = draftCircle ? draftCircle.radius : null;

  const rC1 = (draftRectangle && draftRectangle.c1) || [];
  const rC2 = (draftRectangle && draftRectangle.c2) || [];

  const handleCategoryChange = (val) => {
    setShapeCategory(val);
    if (CATEGORY_COLORS[val]) {
      setShapeColor(CATEGORY_COLORS[val]);
    }
  };

  return (
    <div className="poi-draw-panel">
      <div className="poi-panel-header">
        <span className="poi-panel-title">
          {editingExistingFence ? 'Edit POI' : 'Draw POI'}
        </span>
      </div>

      {/* Draw mode buttons */}
      <div className="poi-field">
        <label>Shape</label>
        <div className="poi-btn-row">
          <Button
            size="small"
            type={drawMode === DRAW_MODE.POLYGON ? 'primary' : 'default'}
            onClick={() => onSetMode(DRAW_MODE.POLYGON)}
            disabled={!canDraw}
          >
            Polygon
          </Button>
          <Button
            size="small"
            type={drawMode === DRAW_MODE.RECTANGLE ? 'primary' : 'default'}
            onClick={() => onSetMode(DRAW_MODE.RECTANGLE)}
            disabled={!canDraw}
          >
            Rectangle
          </Button>
          <Button
            size="small"
            type={drawMode === DRAW_MODE.CIRCLE ? 'primary' : 'default'}
            onClick={() => onSetMode(DRAW_MODE.CIRCLE)}
            disabled={!canDraw}
          >
            Circle
          </Button>
          <Button size="small" onClick={onCancelDraw} disabled={!isDrawingInProgress}>
            Stop
          </Button>
        </div>
      </div>

      {drawMode === DRAW_MODE.POLYGON && (
        <div className="poi-field">
          <Tag color="blue">
            Click to add points · Double-click or click <span style={{ color: '#52c41a', fontWeight: 600 }}>●</span> to close · Enter to finish
          </Tag>
          <div className="poi-btn-row">
            <Button size="small" onClick={onFinishPolygon} disabled={workingPoints.length < 3}>
              Finish
            </Button>
            <Button size="small" onClick={onUndoPoint} disabled={!workingPoints.length}>
              Undo
            </Button>
          </div>
        </div>
      )}
      {drawMode === DRAW_MODE.RECTANGLE && (
        <div className="poi-field">
          <Tag color="blue">
            {rectangleStart ? 'Click second corner to finish' : 'Click first corner to start'}
          </Tag>
        </div>
      )}
      {drawMode === DRAW_MODE.CIRCLE && (
        <div className="poi-field">
          <Tag color="blue">
            {circleCenter ? 'Click edge point to finish' : 'Click center point to start'}
          </Tag>
        </div>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Editing context badge */}
      {editingExistingFence && (
        <div className="poi-field">
          <Tag color="orange">Editing: {editingExistingFence.name}</Tag>
        </div>
      )}

      {/* Name */}
      <div className="poi-field">
        <label>Name</label>
        <Input
          value={shapeName}
          onChange={(e) => setShapeName(e.target.value)}
          placeholder="e.g. Runway 09L"
          size="small"
          disabled={!canDraw}
        />
      </div>

      {/* Category */}
      <div className="poi-field">
        <label>Category</label>
        <Select
          value={shapeCategory}
          onChange={handleCategoryChange}
          size="small"
          style={{ width: '100%' }}
          disabled={!canDraw}
        >
          {CATEGORIES.map((cat) => (
            <Option key={cat} value={cat}>
              <span style={{ marginRight: 6, fontSize: 13 }}>{CATEGORY_ICONS[cat] || '📍'}</span>
              {CATEGORY_LABELS[cat]}
            </Option>
          ))}
        </Select>
      </div>

      {/* Color */}
      <div className="poi-field">
        <label>Color</label>
        <input
          type="color"
          value={shapeColor}
          onChange={(e) => setShapeColor(e.target.value)}
          className="poi-color-input poi-color-input-full"
          disabled={!canDraw}
        />
      </div>

      {/* Stroke weight */}
      <div className="poi-field">
        <label>Stroke Width</label>
        <InputNumber
          min={1}
          max={10}
          value={strokeWeight}
          onChange={(v) => setStrokeWeight(v || 1)}
          size="small"
          style={{ width: '100%' }}
          disabled={!canDraw}
        />
      </div>

      {/* Fill opacity */}
      <div className="poi-field">
        <label>Fill Opacity</label>
        <Slider
          min={0}
          max={1}
          step={0.05}
          value={fillOpacity}
          onChange={setFillOpacity}
          disabled={!canDraw}
        />
      </div>

      {/* Editable geometry — circle */}
      {draftShapeType === DRAW_MODE.CIRCLE && draftCircle && (
        <div className="poi-field">
          <label>Circle Geometry</label>
          <div className="poi-geom-grid">
            <div className="poi-geom-row">
              <span className="poi-geom-label">Radius (m)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={5}
                step={1}
                precision={2}
                value={cRadius}
                onChange={(v) => onApplyCircleGeometry({ center: [cLat, cLng], radius: v })}
                disabled={!canDraw}
              />
            </div>
            <div className="poi-geom-row">
              <span className="poi-geom-label">Latitude (Y1)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-90}
                max={90}
                step={0.0001}
                precision={6}
                value={cLat}
                onChange={(v) => onApplyCircleGeometry({ center: [v, cLng], radius: cRadius })}
                disabled={!canDraw}
              />
            </div>
            <div className="poi-geom-row">
              <span className="poi-geom-label">Longitude (X1)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-180}
                max={180}
                step={0.0001}
                precision={6}
                value={cLng}
                onChange={(v) => onApplyCircleGeometry({ center: [cLat, v], radius: cRadius })}
                disabled={!canDraw}
              />
            </div>
          </div>
        </div>
      )}

      {/* Editable geometry — rectangle */}
      {draftShapeType === DRAW_MODE.RECTANGLE && draftRectangle && (
        <div className="poi-field">
          <label>Rectangle Geometry</label>
          <div className="poi-geom-grid">
            <div className="poi-geom-row">
              <span className="poi-geom-label">Latitude (Y1)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-90}
                max={90}
                step={0.0001}
                precision={6}
                value={rC1.length ? rC1[0] : null}
                onChange={(v) => onApplyRectangleGeometry({ c1: [v, rC1[1]], c2: rC2 })}
                disabled={!canDraw}
              />
            </div>
            <div className="poi-geom-row">
              <span className="poi-geom-label">Longitude (X1)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-180}
                max={180}
                step={0.0001}
                precision={6}
                value={rC1.length ? rC1[1] : null}
                onChange={(v) => onApplyRectangleGeometry({ c1: [rC1[0], v], c2: rC2 })}
                disabled={!canDraw}
              />
            </div>
            <div className="poi-geom-row">
              <span className="poi-geom-label">Latitude (Y2)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-90}
                max={90}
                step={0.0001}
                precision={6}
                value={rC2.length ? rC2[0] : null}
                onChange={(v) => onApplyRectangleGeometry({ c1: rC1, c2: [v, rC2[1]] })}
                disabled={!canDraw}
              />
            </div>
            <div className="poi-geom-row">
              <span className="poi-geom-label">Longitude (X2)</span>
              <InputNumber
                className="poi-geom-input"
                size="small"
                min={-180}
                max={180}
                step={0.0001}
                precision={6}
                value={rC2.length ? rC2[1] : null}
                onChange={(v) => onApplyRectangleGeometry({ c1: rC1, c2: [rC2[0], v] })}
                disabled={!canDraw}
              />
            </div>
          </div>
        </div>
      )}

      {/* Live measurements */}
      <div className="poi-field">
        <label>{drawMode !== DRAW_MODE.NONE ? 'Live Measurements' : 'Shape Measurements'}</label>
        <div className="poi-metrics-box">
          {metricLines.length ? metricLines.map((line, i) => (
            <div key={i} className="poi-metric-line">{line}</div>
          )) : (
            <div className="poi-metrics-empty">
              {drawMode !== DRAW_MODE.NONE ? 'Add points to see measurements.' : 'Draw or select a shape.'}
            </div>
          )}
        </div>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Action buttons */}
      <div className="poi-btn-row poi-btn-row-wrap">
        {!editingExistingFence && canCreate && (
          <Button size="small" type="primary" onClick={onAddToBatch} disabled={!draftFeature}>
            Add to Batch
          </Button>
        )}
        {editingExistingFence && canUpdate && (
          <Button size="small" type="primary" onClick={onUpdateExisting} loading={savingFence} disabled={!draftFeature}>
            Update POI
          </Button>
        )}
        <Button
          size="small"
          onClick={onClearDraft}
          disabled={(!draftFeature && !shapeName) || (!canDraw && !editingExistingFence)}
        >
          {editingExistingFence ? 'Cancel Edit' : 'Clear Draft'}
        </Button>
        {editingExistingFence && canDelete && (
          <Popconfirm
            title="Delete this POI?"
            onConfirm={() => onDelete(editingExistingFence._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" type="danger" disabled={savingFence}>Delete</Button>
          </Popconfirm>
        )}
      </div>

      {canCreate && pendingFences.length > 0 && (
        <div className="poi-field">
          <label>Pending Batch ({pendingFences.length})</label>
          <div className="poi-pending-list">
            {pendingFences.map((item) => (
              <div key={item.localId} className="poi-pending-item">
                <span className="poi-pending-icon">{CATEGORY_ICONS[item.category] || '📍'}</span>
                <span className="poi-pending-name" title={item.name}>{item.name || 'Untitled'}</span>
                <span className="poi-pending-type">{item.shapeType}</span>
                <Button
                  className="poi-pending-remove"
                  size="small"
                  type="link"
                  onClick={() => onRemovePending(item.localId)}
                  disabled={savingAllPending}
                >
                  <Icon type="delete" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {canCreate && (
        <div className="poi-btn-row poi-btn-row-wrap" style={{ marginTop: 4 }}>
          <Button
            size="small"
            type="primary"
            onClick={onSaveAllPending}
            loading={savingAllPending}
            disabled={!pendingFences.length}
          >
            Save All Pending ({pendingFences.length})
          </Button>
        </div>
      )}
    </div>
  );
}
